import { useState } from 'react';
import { crmService } from '../../services/crmService';

// Importable fields and the spreadsheet headers that auto-map to them
const FIELDS = [
  { key: 'first_name',   label: 'First Name *', aliases: ['first name', 'firstname', 'first', 'given name', 'fname'] },
  { key: 'last_name',    label: 'Last Name *',  aliases: ['last name', 'lastname', 'last', 'surname', 'family name', 'lname'] },
  { key: 'email',        label: 'Email',        aliases: ['email', 'e-mail', 'email address', 'work email'] },
  { key: 'title',        label: 'Title',        aliases: ['title', 'job title', 'position', 'role', 'duty title'] },
  { key: 'organization', label: 'Organization', aliases: ['organization', 'org', 'agency', 'company', 'office', 'command', 'unit'] },
  { key: 'rank_code',    label: 'Rank',         aliases: ['rank', 'rank code', 'military rank'] },
  { key: 'grade_code',   label: 'Grade',        aliases: ['grade', 'grade code', 'gs', 'civilian grade'] },
  { key: 'phone',        label: 'Phone',        aliases: ['phone', 'phone number', 'mobile', 'cell', 'telephone'] },
  { key: 'notes',        label: 'Notes',        aliases: ['notes', 'comments', 'remarks', 'description'] },
];

const autoMap = (header) => {
  const h = String(header || '').trim().toLowerCase();
  const f = FIELDS.find(f => f.key === h || f.aliases.includes(h));
  return f ? f.key : '';
};

const TEMPLATE_CSV = 'first_name,last_name,email,title,organization,rank_code,grade_code,phone,notes\n'
  + 'Jane,Doe,jane.doe@example.mil,Program Manager,DARPA,,GS-14,555-0100,Met at SOF Week\n';

const inp = { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '5px 8px', borderRadius: 4, fontSize: 11 };

export default function ContactImport({ onClose, onImported }) {
  const [rows, setRows] = useState(null);       // array of arrays (no header)
  const [headers, setHeaders] = useState([]);   // header row
  const [mapping, setMapping] = useState([]);   // per column: field key or ''
  const [createOrgs, setCreateOrgs] = useState(false);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setResult(null);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const all = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
      const nonEmpty = all.filter(r => r.some(cell => String(cell).trim() !== ''));
      if (nonEmpty.length < 2) { setError('File needs a header row and at least one contact row'); return; }
      const head = nonEmpty[0].map(h => String(h).trim());
      setHeaders(head);
      setMapping(head.map(autoMap));
      setRows(nonEmpty.slice(1));
      setFileName(file.name);
    } catch (err) {
      setError(`Could not read file: ${err.message}`);
    }
    e.target.value = '';
  };

  const mappedContacts = () => rows.map(r => {
    const c = {};
    mapping.forEach((field, i) => {
      if (field && r[i] !== undefined && String(r[i]).trim() !== '') c[field] = String(r[i]).trim();
    });
    return c;
  }).filter(c => c.first_name || c.last_name);

  const upload = async () => {
    const contacts = mappedContacts();
    if (!mapping.includes('first_name') || !mapping.includes('last_name')) {
      setError('Map both First Name and Last Name columns before importing'); return;
    }
    if (contacts.length === 0) { setError('No usable rows found'); return; }
    setBusy(true); setError('');
    const totals = { added: 0, skipped: 0, orgs_created: 0, errors: [] };
    try {
      for (let i = 0; i < contacts.length; i += 200) {
        const batch = contacts.slice(i, i + 200);
        setProgress(`Uploading ${Math.min(i + 200, contacts.length)} of ${contacts.length}…`);
        const r = await crmService.bulkCreateContacts({ contacts: batch, create_missing_orgs: createOrgs });
        totals.added += r.added; totals.skipped += r.skipped; totals.orgs_created += r.orgs_created;
        totals.errors.push(...(r.errors || []).map(e => ({ ...e, row: e.row + i })));
      }
      setResult(totals);
      onImported?.();
    } catch (err) { setError(err.message); } finally { setBusy(false); setProgress(''); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'elephantc2_contacts_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, width: 760, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', color: '#F1F5F9' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 14, color: '#22D3EE' }}>Import Contacts — CSV or Excel</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {result ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>🐘</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#22D3EE', marginBottom: 6 }}>{result.added} contacts joined the herd</div>
              <div style={{ fontSize: 12, color: '#94A3B8' }}>
                {result.skipped} skipped as duplicates{result.orgs_created > 0 ? ` · ${result.orgs_created} organizations created` : ''}
              </div>
              {result.errors.length > 0 && (
                <div style={{ textAlign: 'left', margin: '14px auto 0', maxWidth: 480, background: '#0A0F1C', border: '1px solid #EF444433', borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: '#EF4444', fontWeight: 700, marginBottom: 4 }}>{result.errors.length} row{result.errors.length !== 1 ? 's' : ''} had problems:</div>
                  {result.errors.slice(0, 10).map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#94A3B8' }}>Row {e.row}: {e.error}</div>
                  ))}
                </div>
              )}
              <button onClick={onClose} style={{ marginTop: 18, padding: '8px 24px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Done</button>
            </div>
          ) : !rows ? (
            <div style={{ textAlign: 'center', padding: '26px 0' }}>
              <label style={{ display: 'inline-block', padding: '12px 28px', background: '#0D1F3C', border: '1px dashed #22D3EE66', color: '#22D3EE', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Choose a .csv, .xlsx, or .xls file
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
              </label>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 14, lineHeight: 1.7 }}>
                First row must be headers. Recognized columns: first/last name, email, title,<br />
                organization, rank, grade, phone, notes — or map them manually after upload.
              </div>
              <button onClick={downloadTemplate} style={{ marginTop: 10, background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '5px 14px', cursor: 'pointer', fontSize: 11 }}>
                Download template CSV
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>{fileName} — {rows.length} rows</span>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: '#94A3B8', cursor: 'pointer' }}>
                  <input type="checkbox" checked={createOrgs} onChange={e => setCreateOrgs(e.target.checked)} />
                  Create missing organizations
                </label>
                <button onClick={() => { setRows(null); setHeaders([]); setMapping([]); }} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 10 }}>
                  Choose different file
                </button>
              </div>

              {/* Column mapping + preview */}
              <div style={{ overflowX: 'auto', border: '1px solid #1E2D45', borderRadius: 6 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: '100%' }}>
                  <thead>
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i} style={{ padding: '6px 8px', background: '#0A0F1C', borderBottom: '1px solid #1E2D45', textAlign: 'left', minWidth: 110 }}>
                          <div style={{ color: '#6B7280', fontSize: 9, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h || `(col ${i + 1})`}</div>
                          <select value={mapping[i] || ''} onChange={e => setMapping(m => m.map((v, idx) => idx === i ? e.target.value : v))} style={{ ...inp, width: '100%' }}>
                            <option value="">— skip —</option>
                            {FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((r, ri) => (
                      <tr key={ri}>
                        {headers.map((_, ci) => (
                          <td key={ci} style={{ padding: '4px 8px', borderBottom: '1px solid #11182766', color: '#94A3B8', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {String(r[ci] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 5 && <div style={{ fontSize: 10, color: '#4B5563', marginTop: 4 }}>…and {rows.length - 5} more rows</div>}
              <div style={{ fontSize: 10, color: '#6B7280', marginTop: 8 }}>
                Duplicates are skipped automatically (matched by email, or by name + organization). Authority scores compute from rank/grade on import.
              </div>
            </>
          )}
          {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 10 }}>{error}</div>}
        </div>

        {rows && !result && (
          <div style={{ padding: '10px 18px', borderTop: '1px solid #1E2D45', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            {progress && <span style={{ fontSize: 11, color: '#22D3EE', marginRight: 'auto' }}>{progress}</span>}
            <button onClick={onClose} style={{ padding: '6px 14px', background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            <button onClick={upload} disabled={busy} style={{ padding: '6px 16px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
              {busy ? 'Importing…' : `Import ${mappedContacts().length} Contacts`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
